import { ContactsForm } from "@/components/contact/ContactsForm";
import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { quillEditorStandardProps, quillFormItemProps, selectTagsStandardProps } from "@/utils/componentUtils";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { ibanNormalizeFunction, phoneNumberNormalizeFunction, setErrorsToForm } from "@/utils/formUtils";
import { ALL_WHITE_SPACES_PATTERN } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Button, Col, Divider, Form, Input, Row, Select, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import {
  useCreateSecondaryAgent,
  useGetSecondaryAgent,
  useUpdateSecondaryAgent,
} from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { SecondaryAgentType, secondaryAgentTypeTMap } from "../../../enums";
import { CreateUpdateSecondaryAgent } from "../../../types";
import { convertSecondaryAgentToCreateUpdateObject } from "../../../utils";
import { AgentTypeLongNameTag } from "../../tags/AgenTypeLongNameTag";
import { AgentAddressesFormPart } from "../agent/AgentAddressesFormPart";
import { BasicSecondaryAgentData } from "./BasicSecondaryAgentData";
import { SecondaryAgentRepresentativesFormPart } from "./SecondaryAgentRepresentativesFormPart";

type Props = {
  onCancel: () => void;
  agentId: UUID;
};

export const SecondaryAgentForm = ({ onCancel, agentId }: Props) => {
  const { t } = useTranslation();

  const { data } = useGetSecondaryAgent(agentId);
  const createSecondaryAgent = useCreateSecondaryAgent();
  const updateSecondaryAgent = useUpdateSecondaryAgent();

  const [form] = Form.useForm<CreateUpdateSecondaryAgent>();
  const type = Form.useWatch("type", form);

  useEffect(() => {
    if (data) {
      form.setFieldsValue(convertSecondaryAgentToCreateUpdateObject(data));
    }
  }, [data]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      values.serviceAddress = Object.values(values.serviceAddress ?? {}).some((data) => data !== undefined)
        ? values.serviceAddress
        : undefined;
      values.correspondenceAddress = Object.values(values.correspondenceAddress ?? {}).some(
        (data) => data !== undefined,
      )
        ? values.correspondenceAddress
        : undefined;

      if (data) {
        updateSecondaryAgent.mutate(
          {
            id1: agentId,
            id2: data.id,
            object: values,
          },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "agent.attrs", error.violations),
          },
        );
      } else {
        createSecondaryAgent.mutate(
          {
            id: agentId,
            object: values,
          },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "agent.attrs", error.violations),
          },
        );
      }
    });
  };

  return (
    <>
      <div className="margin-top-large">
        <Form form={form} layout="vertical" name="secondaryAgentForm">
          <HiddenInput name="optimisticLockVersion" />

          <Row>
            <Col span={6}>
              <Form.Item
                name="type"
                label={t("agent.enums.type._label")}
                rules={[validations.notNull]}
                initialValue={SecondaryAgentType.NATURAL}
              >
                <Select<SecondaryAgentType>
                  {...selectTagsStandardProps(secondaryAgentTypeTMap)}
                  options={Object.values(SecondaryAgentType).map((type) => ({
                    value: type,
                    label: <AgentTypeLongNameTag type={type} />,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider titlePlacement="start">{t("agent.titles.basicData")}</Divider>

          <BasicSecondaryAgentData isCreatingNewAgent={!data} />

          <Row>
            <Col span={16}>
              <Row gutter={rowGutter}>
                <Col span={8}>
                  <Form.Item
                    name="email"
                    label={t("agent.attrs.email")}
                    rules={[validations.notBlank, validations.size(1, 254), validations.email]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="phone"
                    label={t("agent.attrs.phone")}
                    rules={[validations.notBlank, validations.mobilePhoneNumber()]}
                    getValueProps={(value) => ({ value: value ? formatPhoneNumber(value) : undefined })}
                    normalize={phoneNumberNormalizeFunction}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="web"
                    label={t("agent.attrs.web")}
                    rules={[validations.size(1, 255), validations.pattern(regexPatterns.webPageRegex)]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={rowGutter}>
                <Col span={16}>
                  <Form.Item
                    name="bankAccountNumber"
                    label={t("agent.attrs.bankAccountNumber")}
                    rules={[validations.size(1, 34), validations.iban]}
                    getValueProps={(value) => ({ value: value ? ibanNormalizeFunction(value) : value })}
                    normalize={(value) => value?.replace(ALL_WHITE_SPACES_PATTERN, "")}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={rowGutter}>
                <Col span={24}>
                  <Form.Item
                    name="note"
                    label={t("agent.attrs.note")}
                    rules={[validations.size(1, 8192)]}
                    {...quillFormItemProps}
                  >
                    <ReactQuill {...quillEditorStandardProps} />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>

          {type === SecondaryAgentType.LEGAL && (
            <>
              <Divider titlePlacement="start">{t("agent.attrs.representatives")}</Divider>

              <SecondaryAgentRepresentativesFormPart
                initialData={data && "representatives" in data ? data.representatives : undefined}
              />
            </>
          )}

          <Divider titlePlacement="start">{t("agent.titles.addresses")}</Divider>

          <AgentAddressesFormPart
            agentType={type}
            hasCorrespondenceAddress={!!data?.correspondenceAddress}
            hasServiceAddress={!!data?.serviceAddress}
          />

          <Divider titlePlacement="start">{t("agent.titles.contacts")}</Divider>

          <ContactsForm />
        </Form>

        <Space className="margin-top-large">
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.parentId !== next.parentId}>
            <Button type="primary" icon={<AntIcon type="save" />} onClick={handleFormSubmit}>
              {t("common.save")}
            </Button>
          </Form.Item>

          <Button onClick={onCancel} icon={<AntIcon type="close" />}>
            {t("common.cancel")}
          </Button>
        </Space>
      </div>
    </>
  );
};
